import { apiClient } from "./client";
import type {
    ApiResponse,
    PageResponse,
    UserType,
    Gender,
    SubscriptionType,
    SubscriptionStatus,
} from "@/types/api";

// ============================================================================
// Types
// ============================================================================

export interface AdminUser {
    id: number;
    email: string;
    userType: UserType;
    fullName: string;
    displayName: string | null;
    phoneNumber: string | null;
    profileImageUrl: string | null;
    dateOfBirth: string | null;
    gender: Gender | null;
    country: string | null;
    city: string | null;
    isActive: boolean;
    isEmailVerified: boolean;
    termsAgreedAt: string | null;
    marketingAgreed: boolean;
    lastLoginAt: string | null;
    createdAt: string;
    updatedAt: string;
    adminRoleId: number | null;
    adminRoleName: string | null;
}

export interface CreateUserRequest {
    email: string;
    password?: string;
    userType: UserType;
    fullName: string;
    displayName?: string;
    phoneNumber?: string;
    dateOfBirth?: string;
    gender?: Gender;
    isActive?: boolean;
    isEmailVerified?: boolean;
}

export interface UpdateUserRequest {
    email?: string;
    userType?: UserType;
    fullName?: string;
    displayName?: string;
    phoneNumber?: string;
    profileImageUrl?: string;
    dateOfBirth?: string;
    gender?: Gender;
    isActive?: boolean;
    isEmailVerified?: boolean;
}

// Organization License Subscription Status
export type OrgLicenseSubscriptionStatus = 'active' | 'cancelled' | 'suspended' | 'expired' | 'terminated';

export interface AdminOrgLicense {
    id: number;
    organizationId: number;
    organizationName: string | null;
    organizationSubdomain: string | null;
    productId: number;
    productName: string | null;
    purchasedSeats: number;
    usedSeats: number;
    validFrom: string;
    validUntil: string | null;
    isActive: boolean;
    purchasedAt: string | null;
    grantedByUserId: number | null;
    allowSelfRegistration: boolean;
    allowAdminAssignment: boolean;
    // Subscription management fields
    subscriptionStatus: OrgLicenseSubscriptionStatus;
    cancelledAt: string | null;
    cancelledByUserId: number | null;
    cancellationReason: string | null;
    terminatedAt: string | null;
    terminatedByUserId: number | null;
    terminationReason: string | null;
    suspendedAt: string | null;
    suspendedByUserId: number | null;
    suspensionReason: string | null;
    restartedAt: string | null;
    restartedByUserId: number | null;
    originalValidUntil: string | null;
    createdAt: string;
    updatedAt: string;
}

// Request types for subscription management
export interface UpdateOrgLicenseSeatsRequest {
    purchasedSeats: number;
}

export interface CancelOrgLicenseRequest {
    reason: string;
}

export interface SuspendOrgLicenseRequest {
    reason: string;
}

export interface RestartOrgLicenseRequest {
    newValidUntil?: string;
}

export interface ExtendOrgLicenseRequest {
    newValidUntil: string;
}

export interface TerminateOrgLicenseRequest {
    reason?: string;
}

export interface AdminSubscription {
    id: number;
    orderId: number;
    productId: number;
    productName: string | null;
    pricingId: number;
    pricingName: string | null;
    ownerUserId: number;
    ownerEmail: string;
    ownerFullName: string;
    organizationId: number | null;
    subscriptionType: SubscriptionType;
    status: SubscriptionStatus;
    maxSeats: number;
    usedSeats: number;
    validFrom: string;
    validUntil: string | null;
    autoRenew: boolean;
    nextBillingDate: string | null;
    cancelledAt: string | null;
    cancelledByUserId: number | null;
    cancellationReason: string | null;
    activatedAt: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface CreateSubscriptionRequest {
    orderId: number;
    productId: number;
    pricingId: number;
    ownerUserId: number;
    organizationId?: number;
    subscriptionType: SubscriptionType;
    status?: SubscriptionStatus;
    maxSeats?: number;
    validFrom: string;
    validUntil?: string;
    autoRenew?: boolean;
    nextBillingDate?: string;
}

export interface UpdateSubscriptionRequest {
    status?: SubscriptionStatus;
    maxSeats?: number;
    validUntil?: string;
    autoRenew?: boolean;
    nextBillingDate?: string;
    cancellationReason?: string;
}

export type HierarchyLevel =
    | "program"
    | "curriculum"
    | "course"
    | "module"
    | "lesson";
export type ContentStatus = "draft" | "ready" | "published" | "archived" | "legacy";

// Product Types
export type ProductStatus = "draft" | "active" | "archived" | "discontinued";
export type PricingType = "one_time" | "subscription";
export type BillingPeriod = "monthly" | "quarterly" | "yearly" | "lifetime";

// Target Audience Categories
export const TARGET_AUDIENCE_OPTIONS = [
    { value: "individual", label: "일반 개인" },
    { value: "parent", label: "학부모" },
    { value: "student", label: "학생" },
    { value: "business", label: "기업/비즈니스" },
    { value: "academy", label: "학원" },
    { value: "school", label: "학교" },
    { value: "all", label: "전체" },
] as const;

export interface AdminProduct {
    id: number;
    name: string;
    slug: string | null;
    shortDescription: string | null;
    description: string | null;
    thumbnailUrl: string | null;
    bannerUrl: string | null;
    previewVideoUrl: string | null;
    features: string[] | null;
    targetAudience: string | null;
    learningOutcomes: string | null;
    status: ProductStatus;
    isActive: boolean;
    isFeatured: boolean;
    hasStockLimit: boolean;
    stockQuantity: number;
    displayOrder: number;
    tags: string | null;
    category: string | null;
    publishedAt: string | null;
    createdAt: string;
    updatedAt: string;
    linkedContentId?: number;
    linkedContentTitle?: string | null;
    isBundle: boolean;
    allowConfigEdit: boolean;
    learningConfigMode?: string | null;
    learningConfigTemplateId?: number | null;
    contentHierarchyLevel: HierarchyLevel;
    lcTrialSetCode?: 'A' | 'B' | 'C' | 'D' | 'E' | null;
    lcTrialCurriculumId?: number | null;
    lcTrialLockedAt?: string | null;
    bundleContents?: ProductContentItem[];
    pricings?: ProductPricing[];
}

export interface AdminLcTrialCurriculum {
    curriculumId: number;
    curriculumTitle: string;
    trialSetCode: 'A' | 'B' | 'C' | 'D' | 'E';
    lessonCount: number;
}

export interface ProductContentItem {
    productId: number;
    contentId: number;
    sequenceOrder: number;
    isRequired: boolean;
    customTitle: string | null;
    customDescription: string | null;
    contentTitle?: string;
    contentHierarchyLevel?: HierarchyLevel;
    thumbnailUrl?: string | null;
}

export interface ProductPricing {
    id: number;
    productId: number;
    name: string;
    pricingType: PricingType;
    billingPeriodMonths: number;
    billingPeriodLabel: string;
    price: number;
    currency: string;
    originalPrice: number | null;
    discountPercentage: number | null;
    maxSeats: number;
    hasTrial: boolean;
    trialDays: number;
    isActive: boolean;
    isDefault: boolean;
    displayOrder: number;
    validFrom: string | null;
    validUntil: string | null;
    isNegotiable: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface CreateProductRequest {
    name: string;
    slug?: string;
    shortDescription?: string;
    description?: string;
    thumbnailUrl?: string;
    bannerUrl?: string;
    previewVideoUrl?: string;
    features?: string[];
    targetAudience?: string;
    learningOutcomes?: string;
    status?: ProductStatus;
    isActive?: boolean;
    isFeatured?: boolean;
    hasStockLimit?: boolean;
    stockQuantity?: number;
    displayOrder?: number;
    tags?: string;
    category?: string;
    linkedContentId?: number;
    isBundle?: boolean;
    allowConfigEdit?: boolean;
    contentHierarchyLevel?: HierarchyLevel;
    lcTrialSetCode?: 'A' | 'B' | 'C' | 'D' | 'E';
    lcTrialCurriculumId?: number;
    clearLcTrialConfiguration?: boolean;
}

export interface UpdateProductRequest extends Partial<CreateProductRequest> {}

export interface CreateProductPricingRequest {
    productId: number;
    name: string;
    pricingType: PricingType;
    billingPeriodMonths: number;
    price: number;
    currency?: string;
    originalPrice?: number;
    discountPercentage?: number;
    maxSeats?: number;
    hasTrial?: boolean;
    trialDays?: number;
    isActive?: boolean;
    isDefault?: boolean;
    displayOrder?: number;
    validFrom?: string;
    validUntil?: string;
    isNegotiable?: boolean;
}

export interface UpdateProductPricingRequest
    extends Partial<Omit<CreateProductPricingRequest, "productId">> {}

// Pricing Configuration Templates
export interface PricingOptionConfig {
    name: string;
    pricingType: PricingType;
    billingPeriod?: string | null;
    billingPeriodMonths: number;
    price: number;
    currency: string;
    originalPrice: number | null;
    discountPercentage: number | null;
    maxSeats: number;
    hasTrial: boolean;
    trialDays: number;
    isDefault: boolean;
    displayOrder: number;
    isNegotiable: boolean;
}

export interface PricingConfiguration {
    version: string;
    pricingOptions: PricingOptionConfig[];
}

export interface PricingTemplate {
    id: number;
    name: string;
    description: string | null;
    pricingOptions: PricingConfiguration;
    isSystemDefault: boolean;
    createdByUserId: number | null;
    createdAt: string;
    updatedAt: string;
}

export interface PricingTemplateListResponse {
    templates: PricingTemplate[];
}

export interface CreatePricingTemplateRequest {
    name: string;
    description?: string;
    pricingOptions: PricingConfiguration;
}

export interface ApplyPricingTemplateRequest {
    replaceExisting: boolean;
}

export interface SavePricingAsTemplateRequest {
    name: string;
    description?: string;
}

export interface AddProductContentRequest {
    productId: number;
    contentId: number;
    sequenceOrder?: number;
    isRequired?: boolean;
    customTitle?: string;
    customDescription?: string;
}

// Bundle content management (bulk operations)
export interface AddProductContentsRequest {
    contentIds: number[];
}

export interface ReorderProductContentsRequest {
    contentOrders: ContentOrderItem[];
}

export interface ContentOrderItem {
    contentId: number;
    sequenceOrder: number;
}

export type ContentOrigin = "notion" | "direct";

export interface AdminContentAncestor {
    id: number;
    title: string;
}

export interface AdminContent {
    id: number;
    notionPageId: string | null;
    hierarchyLevel: HierarchyLevel;
    status: ContentStatus;
    title: string;
    bareTitle: string | null;
    description: string | null;
    shortDescription: string | null;
    starRating: number | null;
    learningObjectives: string | null;
    concepts: string | null;
    estimatedDurationMinutes: number | null;
    thumbnailUrl: string | null;
    isActive: boolean;
    lcCumulativeAssessmentEnabled?: boolean;
    parentId: number | null;
    qid: string | null;
    overallSequenceOrder: number | null;
    sequenceOrderInParent: number | null;
    origin: ContentOrigin | null;
    attachedParentsCount?: number;  // 1 = primary only, ≥2 = shared across multiple parents
    curriculumAncestors?: AdminContentAncestor[];
    createdAt: string;
    updatedAt: string;
}

// Curriculum Builder Types
export interface CreateDirectContentRequest {
    hierarchyLevel: "curriculum" | "course" | "module";
    title: string;
    bareTitle?: string;
    description?: string;
    shortDescription?: string;
    parentId?: number;
    estimatedDurationMinutes?: number;
    thumbnailUrl?: string;
    learningObjectives?: string;
    concepts?: string;
}

export interface UpdateDirectContentRequest {
    title?: string;
    bareTitle?: string;
    description?: string;
    shortDescription?: string;
    estimatedDurationMinutes?: number;
    thumbnailUrl?: string;
    learningObjectives?: string;
    concepts?: string;
    status?: string;
    lcCumulativeAssessmentEnabled?: boolean;
}

export interface ChildOrderItem {
    contentId: number;
    sequenceOrder: number;
}

// Organization Types (for product management)
export interface OrganizationSimple {
    id: number;
    name: string;
    displayName: string | null;
    organizationType: string;
    subdomain: string | null;
    isActive: boolean;
}

// Organization Types (for organization management)
export type OrganizationType = 'company' | 'academy' | 'school' | 'institution' | 'other';
export type OrganizationStatus = 'active' | 'suspended' | 'closed';
export type LcOperationMode = 'PRODUCTION' | 'TEST';

export interface AdminOrganization {
    id: number;
    name: string;
    displayName: string | null;
    representativeName: string | null;
    organizationType: string;
    businessCategory: string | null;
    subdomain: string | null;
    email: string | null;
    phoneNumber: string | null;
    websiteUrl: string | null;
    logoUrl: string | null;
    primaryColor: string | null;
    contractStartDate: string | null;
    contractEndDate: string | null;
    maxSeats: number;
    lcOperationMode: LcOperationMode;
    currentSeats: number;
    status: string;
    isActive: boolean;
    createdAt: string | null;
    devPort: number | null;  // 개발 환경용 localhost 포트
    languageMode: string;  // ko, en, both
    lcTrialEnabled: boolean;
    lcMallEnabled: boolean;
    aiqTriangleFirstThird?: number | null;   // AIQ 삼각형 첫 구간 경계 (0-100)
    aiqTriangleSecondThird?: number | null;  // AIQ 삼각형 두번째 구간 경계 (0-firstThird)
    aiqEstimatedMean?: number | null;        // AIQ 지수 변환용 모집단 평균 추정값
    aiqEstimatedSigma?: number | null;       // AIQ 지수 변환용 모집단 표준편차 추정값
    aiqAdvancedBaseScore?: number | null;    // Advanced Score factor 하한
    aiqAdvancedCeilingScore?: number | null; // Advanced Score factor 상한
}

export interface CreateOrganizationRequest {
    name: string;
    displayName?: string;
    representativeName?: string;
    organizationType?: string;
    businessCategory?: string;
    subdomain: string;
    email?: string;
    phoneNumber?: string;
    websiteUrl?: string;
    logoUrl?: string;
    primaryColor?: string;
    contractStartDate?: string;
    contractEndDate?: string;
    maxSeats?: number;
    lcOperationMode?: LcOperationMode;
    devPort?: number;  // 개발 환경용 localhost 포트
    languageMode?: string;  // ko, en, both
    lcTrialEnabled?: boolean;
    lcMallEnabled?: boolean;
    aiqTriangleFirstThird?: number | null;   // AIQ 삼각형 첫 구간 경계 (0-100)
    aiqTriangleSecondThird?: number | null;  // AIQ 삼각형 두번째 구간 경계 (0-firstThird)
    aiqEstimatedMean?: number | null;        // AIQ 지수 변환용 모집단 평균 추정값
    aiqEstimatedSigma?: number | null;       // AIQ 지수 변환용 모집단 표준편차 추정값
    aiqAdvancedBaseScore?: number | null;    // Advanced Score factor 하한
    aiqAdvancedCeilingScore?: number | null; // Advanced Score factor 상한
}

export interface UpdateOrganizationRequest extends Partial<CreateOrganizationRequest> {}

export interface OrganizationSettingsResponse {
    organizationId: number;
    settings: Record<string, unknown>;
    allowedDomains: string[];
    ssoEnabled: boolean;
    ssoProvider: string | null;
    lcMallEnabled: boolean;
    lcTrialEnabled: boolean;
}

export interface AdminLcTrialProgramSummary {
    enabled: boolean;
    firstGrantedOn: string | null;
    closesOn: string | null;
    grantedCount: number;
    remainingGrants: number;
    open: boolean;
}

export interface OrganizationLicense {
    id: number;
    organizationId: number;
    productId: number;
    productName: string | null;
    curriculumContentId: number | null;
    curriculumTitle: string | null;
    validFrom: string;
    validUntil: string | null;
    isActive: boolean;
    createdAt: string | null;
    purchasedAt: string | null;
    purchasedSeats: number;
    usedSeats: number;
    allowSelfRegistration: boolean;
    allowAdminAssignment: boolean;
}

export interface CreateLicenseRequest {
    productId: number;
    validFrom: string;
    validUntil?: string;
}

// Data Reset Types
export interface DataResetOptions {
    keepMembers: boolean;
    keepLicenses: boolean;
    includeOrders: boolean;
    skipBackup: boolean;
}

export interface DataResetTableCount {
    tableName: string;
    count: number;
}

export interface DataResetPhaseCount {
    phase: string;
    tables: DataResetTableCount[];
    skipped: boolean;
    skipReason: string | null;
}

export interface DataResetPreview {
    organizationId: number;
    subdomain: string;
    environment: string;
    phases: DataResetPhaseCount[];
    totalRows: number;
}

export interface DataResetBackupInfo {
    backupId: string;
    createdAt: string;
    environment: string;
    rowCount: number;
    localPath?: string;
    s3Location?: string;
}

export interface DataResetResult {
    success: boolean;
    backupInfo: DataResetBackupInfo | null;
    deletedCounts: Record<string, number>;
    totalDeleted: number;
    postCleanCounts: DataResetPreview;
}

export interface BackupListItem {
    backupId: string;
    fileName: string;
    createdAt: string | null;
    environment: string | null;
    source: string;
    fileSize: number;
}

export interface BackupRestorePreview {
    backupId: string;
    source: string;
    metadata: Record<string, unknown> | null;
    tables: { tableName: string; rowCount: number }[];
    totalRows: number;
}

export interface BackupRestoreResult {
    success: boolean;
    backupId: string;
    restoredCounts: Record<string, number>;
    totalRestored: number;
    errors: string[];
}

export interface OrganizationMember {
    userId: number;
    email: string;
    fullName: string;
    departmentId: number | null;
    departmentName: string | null;
    employeeId: string | null;
    position: string | null;
    role: string;
    status: string;
    joinedAt: string | null;
    createdAt: string | null;
}

export interface ProductOrganization {
    organizationId: number;
    organizationName: string;
    organizationDisplayName: string | null;
    organizationType: string;
    subdomain: string | null;
    licenseId: number;
    validFrom: string;
    validUntil: string | null;
    isActive: boolean;
    hasActiveSubscription: boolean; // 실제 구독 여부 (money icon 표시용)
}

export interface OpenToOrgRequest {
    organizationIds: number[];
    validFrom?: string;
    validUntil?: string | null;
}

export interface BulkActivateResponse {
    activatedCount: number;
    productIds: number[];
}

export interface DashboardStats {
    totalUsers: number;
    activeUsers: number;
    totalSubscriptions: number;
    activeSubscriptions: number;
    totalContents: number;
    publishedContents: number;
    usersByType: Record<string, number>;
    subscriptionsByStatus: Record<string, number>;
}

export type AdminLcCenterStatus = 'EDUCATION' | 'OPERATING' | 'TERMINATED' | 'ON_HOLD';

export interface AdminLcCenterDashboardRow {
    organizationId: number;
    subdomain: string | null;
    centerName: string;
    region: string | null;
    ownerName: string | null;
    ownerPhoneNumber: string | null;
    waitingLearners: number;
    enrolledLearners: number;
    activeClasses: number;
    status: AdminLcCenterStatus;
    openedOn: string | null;
    franchiseFeeAmount: number | null;
    contentRevenueAmount: number;
    mallRevenueAmount: number;
    totalRevenueAmount: number | null;
}

// ============================================================================
// API Functions
// ============================================================================

export const adminApi = {
    // Dashboard
    getDashboardStats: async (): Promise<DashboardStats> => {
        const response = await apiClient.get<ApiResponse<DashboardStats>>(
            "/admin/dashboard/stats"
        );
        if (!response.data.data) {
            throw new Error("Failed to fetch dashboard stats");
        }
        return response.data.data;
    },

    getLcCenterDashboard: async (
        params: {
            page?: number;
            size?: number;
            keyword?: string;
            status?: AdminLcCenterStatus;
            includeTest?: boolean;
        } = {}
    ): Promise<PageResponse<AdminLcCenterDashboardRow>> => {
        const response = await apiClient.get<ApiResponse<PageResponse<AdminLcCenterDashboardRow>>>(
            "/admin/dashboard/lc-centers",
            { params }
        );
        if (!response.data.data) {
            throw new Error("Failed to fetch LC center dashboard");
        }
        return response.data.data;
    },

    // Users
    getUsers: async (
        params: {
            keyword?: string;
            userType?: UserType;
            organizationId?: number;
            isActive?: boolean;
            page?: number;
            size?: number;
        } = {}
    ): Promise<PageResponse<AdminUser>> => {
        const response = await apiClient.get<
            ApiResponse<PageResponse<AdminUser>>
        >("/admin/users", { params });
        if (!response.data.data) {
            throw new Error("Failed to fetch users");
        }
        return response.data.data;
    },

    getUserById: async (id: number): Promise<AdminUser> => {
        const response = await apiClient.get<ApiResponse<AdminUser>>(
            `/admin/users/${id}`
        );
        if (!response.data.data) {
            throw new Error("User not found");
        }
        return response.data.data;
    },

    createUser: async (data: CreateUserRequest): Promise<AdminUser> => {
        const response = await apiClient.post<ApiResponse<AdminUser>>(
            "/admin/users",
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to create user");
        }
        return response.data.data;
    },

    updateUser: async (
        id: number,
        data: UpdateUserRequest
    ): Promise<AdminUser> => {
        const response = await apiClient.put<ApiResponse<AdminUser>>(
            `/admin/users/${id}`,
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to update user");
        }
        return response.data.data;
    },

    toggleUserStatus: async (id: number): Promise<AdminUser> => {
        const response = await apiClient.patch<ApiResponse<AdminUser>>(
            `/admin/users/${id}/toggle-status`
        );
        if (!response.data.data) {
            throw new Error("Failed to toggle user status");
        }
        return response.data.data;
    },

    changeUserPassword: async (id: number, newPassword: string): Promise<{ message: string }> => {
        const response = await apiClient.put<ApiResponse<{ message: string }>>(
            `/admin/users/${id}/password`,
            { newPassword }
        );
        if (!response.data.data) {
            throw new Error("Failed to change password");
        }
        return response.data.data;
    },

    deleteUser: async (id: number): Promise<void> => {
        await apiClient.delete(`/admin/users/${id}`);
    },

    // Subscriptions
    getSubscriptions: async (
        params: {
            ownerId?: number;
            productId?: number;
            status?: SubscriptionStatus;
            subscriptionType?: SubscriptionType;
            page?: number;
            size?: number;
        } = {}
    ): Promise<PageResponse<AdminSubscription>> => {
        const response = await apiClient.get<
            ApiResponse<PageResponse<AdminSubscription>>
        >("/admin/subscriptions", { params });
        if (!response.data.data) {
            throw new Error("Failed to fetch subscriptions");
        }
        return response.data.data;
    },

    getSubscriptionById: async (id: number): Promise<AdminSubscription> => {
        const response = await apiClient.get<ApiResponse<AdminSubscription>>(
            `/admin/subscriptions/${id}`
        );
        if (!response.data.data) {
            throw new Error("Subscription not found");
        }
        return response.data.data;
    },

    createSubscription: async (
        data: CreateSubscriptionRequest
    ): Promise<AdminSubscription> => {
        const response = await apiClient.post<ApiResponse<AdminSubscription>>(
            "/admin/subscriptions",
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to create subscription");
        }
        return response.data.data;
    },

    updateSubscription: async (
        id: number,
        data: UpdateSubscriptionRequest
    ): Promise<AdminSubscription> => {
        const response = await apiClient.put<ApiResponse<AdminSubscription>>(
            `/admin/subscriptions/${id}`,
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to update subscription");
        }
        return response.data.data;
    },

    cancelSubscription: async (
        id: number,
        reason?: string
    ): Promise<AdminSubscription> => {
        const response = await apiClient.patch<ApiResponse<AdminSubscription>>(
            `/admin/subscriptions/${id}/cancel`,
            null,
            { params: { reason } }
        );
        if (!response.data.data) {
            throw new Error("Failed to cancel subscription");
        }
        return response.data.data;
    },

    deleteSubscription: async (id: number): Promise<void> => {
        await apiClient.delete(`/admin/subscriptions/${id}`);
    },

    // Contents
    getContents: async (
        params: {
            keyword?: string;
            lessonKeyword?: string;
            hierarchyLevel?: HierarchyLevel;
            status?: ContentStatus;
            page?: number;
            size?: number;
        } = {}
    ): Promise<PageResponse<AdminContent>> => {
        const response = await apiClient.get<
            ApiResponse<PageResponse<AdminContent>>
        >("/admin/contents", { params });
        if (!response.data.data) {
            throw new Error("Failed to fetch contents");
        }
        return response.data.data;
    },

    getContentById: async (id: number): Promise<AdminContent> => {
        const response = await apiClient.get<ApiResponse<AdminContent>>(
            `/admin/contents/${id}`
        );
        if (!response.data.data) {
            throw new Error("Content not found");
        }
        return response.data.data;
    },

    getContentsByLevel: async (
        level: HierarchyLevel,
        includeInactive: boolean = false
    ): Promise<AdminContent[]> => {
        const response = await apiClient.get<ApiResponse<AdminContent[]>>(
            `/admin/contents/level/${level}`,
            {
                params: { includeInactive },
            }
        );
        return response.data.data || [];
    },

    getChildContents: async (parentId: number): Promise<AdminContent[]> => {
        const response = await apiClient.get<ApiResponse<AdminContent[]>>(
            `/admin/contents/${parentId}/children`
        );
        return response.data.data || [];
    },

    updateContentStatus: async (
        id: number,
        status: ContentStatus
    ): Promise<AdminContent> => {
        const response = await apiClient.patch<ApiResponse<AdminContent>>(
            `/admin/contents/${id}/status`,
            null,
            { params: { status } }
        );
        if (!response.data.data) {
            throw new Error("Failed to update status");
        }
        return response.data.data;
    },

    bulkUpdateContentStatus: async (
        ids: number[],
        status: ContentStatus
    ): Promise<{ updatedCount: number; ids: number[] }> => {
        const response = await apiClient.patch<
            ApiResponse<{ updatedCount: number; ids: number[] }>
        >("/admin/contents/bulk-status", { ids, status });
        if (!response.data.data) {
            throw new Error("Failed to bulk update status");
        }
        return response.data.data;
    },

    getProductsByContentId: async (
        contentId: number
    ): Promise<AdminProduct[]> => {
        const response = await apiClient.get<ApiResponse<AdminProduct[]>>(
            `/admin/contents/${contentId}/products`
        );
        return response.data.data || [];
    },

    // Products
    getProducts: async (
        params: {
            keyword?: string;
            status?: ProductStatus;
            targetAudience?: string;
            isActive?: boolean;
            isFeatured?: boolean;
            page?: number;
            size?: number;
        } = {}
    ): Promise<PageResponse<AdminProduct>> => {
        const response = await apiClient.get<
            ApiResponse<PageResponse<AdminProduct>>
        >("/admin/products", { params });
        if (!response.data.data) {
            throw new Error("Failed to fetch products");
        }
        return response.data.data;
    },

    getProductById: async (id: number): Promise<AdminProduct> => {
        const response = await apiClient.get<ApiResponse<AdminProduct>>(
            `/admin/products/${id}`
        );
        if (!response.data.data) {
            throw new Error("Product not found");
        }
        return response.data.data;
    },

    getLcTrialCurricula: async (): Promise<AdminLcTrialCurriculum[]> => {
        const response = await apiClient.get<ApiResponse<AdminLcTrialCurriculum[]>>(
            "/admin/products/lc-trial-curricula"
        );
        return response.data.data || [];
    },

    createProduct: async (
        data: CreateProductRequest
    ): Promise<AdminProduct> => {
        const response = await apiClient.post<ApiResponse<AdminProduct>>(
            "/admin/products",
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to create product");
        }
        return response.data.data;
    },

    updateProduct: async (
        id: number,
        data: UpdateProductRequest
    ): Promise<AdminProduct> => {
        const response = await apiClient.put<ApiResponse<AdminProduct>>(
            `/admin/products/${id}`,
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to update product");
        }
        return response.data.data;
    },

    toggleProductStatus: async (id: number): Promise<AdminProduct> => {
        const response = await apiClient.patch<ApiResponse<AdminProduct>>(
            `/admin/products/${id}/toggle-status`
        );
        if (!response.data.data) {
            throw new Error("Failed to toggle product status");
        }
        return response.data.data;
    },

    deleteProduct: async (id: number): Promise<void> => {
        await apiClient.delete(`/admin/products/${id}`);
    },

    // Product Pricing
    getProductPricings: async (
        productId: number
    ): Promise<ProductPricing[]> => {
        const response = await apiClient.get<ApiResponse<ProductPricing[]>>(
            `/admin/products/${productId}/pricings`
        );
        return response.data.data || [];
    },

    createProductPricing: async (
        data: CreateProductPricingRequest
    ): Promise<ProductPricing> => {
        const response = await apiClient.post<ApiResponse<ProductPricing>>(
            `/admin/products/${data.productId}/pricings`,
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to create pricing");
        }
        return response.data.data;
    },

    updateProductPricing: async (
        productId: number,
        pricingId: number,
        data: UpdateProductPricingRequest
    ): Promise<ProductPricing> => {
        const response = await apiClient.put<ApiResponse<ProductPricing>>(
            `/admin/products/${productId}/pricings/${pricingId}`,
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to update pricing");
        }
        return response.data.data;
    },

    deleteProductPricing: async (
        productId: number,
        pricingId: number
    ): Promise<void> => {
        await apiClient.delete(
            `/admin/products/${productId}/pricings/${pricingId}`
        );
    },

    // Pricing Configuration Templates
    getPricingTemplates: async (): Promise<PricingTemplate[]> => {
        const response = await apiClient.get<ApiResponse<PricingTemplateListResponse>>(
            `/configurations/pricing-templates`
        );
        return response.data.data?.templates || [];
    },

    getPricingTemplate: async (templateId: number): Promise<PricingTemplate> => {
        const response = await apiClient.get<ApiResponse<PricingTemplate>>(
            `/configurations/pricing-templates/${templateId}`
        );
        if (!response.data.data) {
            throw new Error("Failed to get pricing template");
        }
        return response.data.data;
    },

    createPricingTemplate: async (
        data: CreatePricingTemplateRequest
    ): Promise<PricingTemplate> => {
        const response = await apiClient.post<ApiResponse<PricingTemplate>>(
            `/configurations/pricing-templates`,
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to create pricing template");
        }
        return response.data.data;
    },

    updatePricingTemplate: async (
        templateId: number,
        data: CreatePricingTemplateRequest
    ): Promise<PricingTemplate> => {
        const response = await apiClient.put<ApiResponse<PricingTemplate>>(
            `/configurations/pricing-templates/${templateId}`,
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to update pricing template");
        }
        return response.data.data;
    },

    deletePricingTemplate: async (templateId: number): Promise<void> => {
        await apiClient.delete(`/configurations/pricing-templates/${templateId}`);
    },

    applyPricingTemplate: async (
        templateId: number,
        productId: number,
        replaceExisting: boolean = true
    ): Promise<ProductPricing[]> => {
        const response = await apiClient.post<ApiResponse<ProductPricing[]>>(
            `/configurations/pricing-templates/${templateId}/apply/${productId}`,
            { replaceExisting }
        );
        return response.data.data || [];
    },

    savePricingAsTemplate: async (
        productId: number,
        data: SavePricingAsTemplateRequest
    ): Promise<PricingTemplate> => {
        const response = await apiClient.post<ApiResponse<PricingTemplate>>(
            `/configurations/pricing-templates/from-product/${productId}`,
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to save pricing as template");
        }
        return response.data.data;
    },

    // Product Contents
    getProductContents: async (
        productId: number
    ): Promise<ProductContentItem[]> => {
        const response = await apiClient.get<ApiResponse<ProductContentItem[]>>(
            `/admin/products/${productId}/contents`
        );
        return response.data.data || [];
    },

    addProductContent: async (
        data: AddProductContentRequest
    ): Promise<ProductContentItem> => {
        const response = await apiClient.post<ApiResponse<ProductContentItem>>(
            `/admin/products/${data.productId}/contents`,
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to add content to product");
        }
        return response.data.data;
    },

    updateProductContent: async (
        productId: number,
        contentId: number,
        data: Partial<AddProductContentRequest>
    ): Promise<ProductContentItem> => {
        const response = await apiClient.put<ApiResponse<ProductContentItem>>(
            `/admin/products/${productId}/contents/${contentId}`,
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to update product content");
        }
        return response.data.data;
    },

    removeProductContent: async (
        productId: number,
        contentId: number
    ): Promise<void> => {
        await apiClient.delete(
            `/admin/products/${productId}/contents/${contentId}`
        );
    },

    // Bundle content management (bulk operations)
    addProductContents: async (
        productId: number,
        data: AddProductContentsRequest
    ): Promise<ProductContentItem[]> => {
        const response = await apiClient.post<ApiResponse<ProductContentItem[]>>(
            `/admin/products/${productId}/contents`,
            data
        );
        return response.data.data || [];
    },

    reorderProductContents: async (
        productId: number,
        data: ReorderProductContentsRequest
    ): Promise<ProductContentItem[]> => {
        const response = await apiClient.put<ApiResponse<ProductContentItem[]>>(
            `/admin/products/${productId}/contents/reorder`,
            data
        );
        return response.data.data || [];
    },

    // Organizations (for product management)
    getAllOrganizations: async (): Promise<OrganizationSimple[]> => {
        const response = await apiClient.get<ApiResponse<OrganizationSimple[]>>(
            "/admin/products/organizations"
        );
        return response.data.data || [];
    },

    getProductOrganizations: async (
        productId: number
    ): Promise<ProductOrganization[]> => {
        const response = await apiClient.get<
            ApiResponse<ProductOrganization[]>
        >(`/admin/products/${productId}/organizations`);
        return response.data.data || [];
    },

    openProductToOrganizations: async (
        productId: number,
        data: OpenToOrgRequest
    ): Promise<ProductOrganization[]> => {
        const response = await apiClient.post<
            ApiResponse<ProductOrganization[]>
        >(`/admin/products/${productId}/organizations`, data);
        return response.data.data || [];
    },

    closeProductFromOrganization: async (
        productId: number,
        organizationId: number
    ): Promise<void> => {
        await apiClient.delete(
            `/admin/products/${productId}/organizations/${organizationId}`
        );
    },

    bulkActivateProducts: async (
        productIds: number[]
    ): Promise<BulkActivateResponse> => {
        const response = await apiClient.post<
            ApiResponse<BulkActivateResponse>
        >("/admin/products/bulk-activate", { productIds });
        if (!response.data.data) {
            throw new Error("Failed to bulk activate products");
        }
        return response.data.data;
    },

    // ============================================================================
    // Organizations
    // ============================================================================

    getOrganizations: async (): Promise<AdminOrganization[]> => {
        const response = await apiClient.get<ApiResponse<AdminOrganization[]>>(
            "/admin/organizations"
        );
        return response.data.data || [];
    },

    getOrganizationById: async (id: number): Promise<AdminOrganization> => {
        const response = await apiClient.get<ApiResponse<AdminOrganization>>(
            `/admin/organizations/${id}`
        );
        if (!response.data.data) {
            throw new Error("Organization not found");
        }
        return response.data.data;
    },

    getOrganizationLcTrialProgram: async (
        id: number
    ): Promise<AdminLcTrialProgramSummary> => {
        const response = await apiClient.get<ApiResponse<AdminLcTrialProgramSummary>>(
            `/admin/organizations/${id}/lc-free-trial-program`
        );
        if (!response.data.data) {
            throw new Error("LC free trial program not found");
        }
        return response.data.data;
    },

    createOrganization: async (
        data: CreateOrganizationRequest
    ): Promise<AdminOrganization> => {
        const response = await apiClient.post<ApiResponse<AdminOrganization>>(
            "/admin/organizations",
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to create organization");
        }
        return response.data.data;
    },

    updateOrganization: async (
        id: number,
        data: UpdateOrganizationRequest
    ): Promise<AdminOrganization> => {
        const response = await apiClient.put<ApiResponse<AdminOrganization>>(
            `/admin/organizations/${id}`,
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to update organization");
        }
        return response.data.data;
    },

    updateOrganizationSettings: async (
        id: number,
        data: { lcMallEnabled?: boolean; lcTrialEnabled?: boolean }
    ): Promise<OrganizationSettingsResponse> => {
        const response = await apiClient.put<ApiResponse<OrganizationSettingsResponse>>(
            `/admin/organizations/${id}/settings`,
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to update organization settings");
        }
        return response.data.data;
    },

    deactivateOrganization: async (id: number): Promise<void> => {
        await apiClient.delete(`/admin/organizations/${id}`);
    },

    // Data Reset
    previewResetData: async (
        orgId: number,
        options: DataResetOptions
    ): Promise<DataResetPreview> => {
        const response = await apiClient.post<ApiResponse<DataResetPreview>>(
            `/admin/organizations/${orgId}/reset-data/preview`,
            options
        );
        if (!response.data.data) {
            throw new Error("Failed to preview reset data");
        }
        return response.data.data;
    },

    executeResetData: async (
        orgId: number,
        options: DataResetOptions
    ): Promise<DataResetResult> => {
        const response = await apiClient.post<ApiResponse<DataResetResult>>(
            `/admin/organizations/${orgId}/reset-data/execute`,
            options
        );
        if (!response.data.data) {
            throw new Error("Failed to execute reset data");
        }
        return response.data.data;
    },

    // Backup Restore
    listBackups: async (orgId: number): Promise<BackupListItem[]> => {
        const response = await apiClient.get<ApiResponse<BackupListItem[]>>(
            `/admin/organizations/${orgId}/reset-data/backups`
        );
        return response.data.data ?? [];
    },

    previewRestore: async (orgId: number, backupId: string): Promise<BackupRestorePreview> => {
        const response = await apiClient.get<ApiResponse<BackupRestorePreview>>(
            `/admin/organizations/${orgId}/reset-data/backups/${backupId}/preview`
        );
        if (!response.data.data) {
            throw new Error("Failed to preview restore");
        }
        return response.data.data;
    },

    executeRestore: async (orgId: number, backupId: string): Promise<BackupRestoreResult> => {
        const response = await apiClient.post<ApiResponse<BackupRestoreResult>>(
            `/admin/organizations/${orgId}/reset-data/backups/${backupId}/restore`
        );
        if (!response.data.data) {
            throw new Error("Failed to execute restore");
        }
        return response.data.data;
    },

    // Organization Licenses
    getOrganizationLicenses: async (
        organizationId: number
    ): Promise<OrganizationLicense[]> => {
        const response = await apiClient.get<ApiResponse<OrganizationLicense[]>>(
            `/admin/organizations/${organizationId}/licenses`
        );
        return response.data.data || [];
    },

    grantLicense: async (
        organizationId: number,
        data: CreateLicenseRequest
    ): Promise<OrganizationLicense> => {
        const response = await apiClient.post<ApiResponse<OrganizationLicense>>(
            `/admin/organizations/${organizationId}/licenses`,
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to grant license");
        }
        return response.data.data;
    },

    revokeLicense: async (licenseId: number): Promise<void> => {
        await apiClient.delete(`/admin/organizations/licenses/${licenseId}`);
    },

    // Organization Members
    getOrganizationMembers: async (
        organizationId: number
    ): Promise<OrganizationMember[]> => {
        const response = await apiClient.get<ApiResponse<OrganizationMember[]>>(
            `/admin/organizations/${organizationId}/members`
        );
        return response.data.data || [];
    },

    addOrganizationMember: async (
        organizationId: number,
        data: { userId: number; role: string }
    ): Promise<OrganizationMember> => {
        const response = await apiClient.post<ApiResponse<OrganizationMember>>(
            `/admin/organizations/${organizationId}/members/add`,
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to add member");
        }
        return response.data.data;
    },

    resetMemberPassword: async (
        organizationId: number,
        userId: number
    ): Promise<{ temporaryPassword: string }> => {
        const response = await apiClient.post<
            ApiResponse<{ temporaryPassword: string }>
        >(`/admin/organizations/${organizationId}/members/${userId}/reset-password`);
        if (!response.data.data) {
            throw new Error("Failed to reset password");
        }
        return response.data.data;
    },

    updateMemberRole: async (
        organizationId: number,
        userId: number,
        role: string
    ): Promise<void> => {
        await apiClient.patch(
            `/admin/organizations/${organizationId}/members/${userId}/role`,
            { role }
        );
    },

    removeOrganizationMember: async (
        organizationId: number,
        userId: number
    ): Promise<void> => {
        await apiClient.delete(
            `/admin/organizations/${organizationId}/members/${userId}`
        );
    },

    // ============================================================================
    // File Upload
    // ============================================================================

    uploadImage: async (
        file: File,
        type: string = "logo"
    ): Promise<{ url: string; filename: string }> => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("type", type);

        const response = await apiClient.post<
            ApiResponse<{ url: string; filename: string }>
        >("/admin/upload/image", formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
        if (!response.data.data) {
            throw new Error("Failed to upload image");
        }
        return response.data.data;
    },

    // ============================================================================
    // Content Hierarchy
    // ============================================================================

    getContentHierarchy: async (contentId: number, includeInactive: boolean = true): Promise<ContentHierarchyResponse> => {
        const response = await apiClient.get<ApiResponse<ContentHierarchyResponse>>(
            `/content/${contentId}/hierarchy`,
            { params: { includeInactive } }
        );
        if (!response.data.data) {
            throw new Error("Content not found");
        }
        return response.data.data;
    },

    // Organization Licenses
    getOrgLicenses: async (
        params: {
            organizationId?: number;
            productId?: number;
            isActive?: boolean;
            isPurchased?: boolean;
            subscriptionStatus?: OrgLicenseSubscriptionStatus;
            page?: number;
            size?: number;
        } = {}
    ): Promise<PageResponse<AdminOrgLicense>> => {
        const response = await apiClient.get<
            ApiResponse<PageResponse<AdminOrgLicense>>
        >("/admin/organization-licenses", { params });
        if (!response.data.data) {
            throw new Error("Failed to fetch organization licenses");
        }
        return response.data.data;
    },

    getOrgLicenseById: async (id: number): Promise<AdminOrgLicense> => {
        const response = await apiClient.get<ApiResponse<AdminOrgLicense>>(
            `/admin/organization-licenses/${id}`
        );
        if (!response.data.data) {
            throw new Error("Organization license not found");
        }
        return response.data.data;
    },

    updateOrgLicenseSeats: async (
        id: number,
        data: UpdateOrgLicenseSeatsRequest
    ): Promise<AdminOrgLicense> => {
        const response = await apiClient.patch<ApiResponse<AdminOrgLicense>>(
            `/admin/organization-licenses/${id}/seats`,
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to update seats");
        }
        return response.data.data;
    },

    cancelOrgLicense: async (
        id: number,
        data: CancelOrgLicenseRequest
    ): Promise<AdminOrgLicense> => {
        const response = await apiClient.patch<ApiResponse<AdminOrgLicense>>(
            `/admin/organization-licenses/${id}/cancel`,
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to cancel subscription");
        }
        return response.data.data;
    },

    suspendOrgLicense: async (
        id: number,
        data: SuspendOrgLicenseRequest
    ): Promise<AdminOrgLicense> => {
        const response = await apiClient.patch<ApiResponse<AdminOrgLicense>>(
            `/admin/organization-licenses/${id}/suspend`,
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to suspend subscription");
        }
        return response.data.data;
    },

    restartOrgLicense: async (
        id: number,
        data: RestartOrgLicenseRequest
    ): Promise<AdminOrgLicense> => {
        const response = await apiClient.patch<ApiResponse<AdminOrgLicense>>(
            `/admin/organization-licenses/${id}/restart`,
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to restart subscription");
        }
        return response.data.data;
    },

    extendOrgLicense: async (
        id: number,
        data: ExtendOrgLicenseRequest
    ): Promise<AdminOrgLicense> => {
        const response = await apiClient.patch<ApiResponse<AdminOrgLicense>>(
            `/admin/organization-licenses/${id}/extend`,
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to extend subscription");
        }
        return response.data.data;
    },

    terminateOrgLicense: async (
        id: number,
        data: TerminateOrgLicenseRequest
    ): Promise<AdminOrgLicense> => {
        const response = await apiClient.patch<ApiResponse<AdminOrgLicense>>(
            `/admin/organization-licenses/${id}/terminate`,
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to terminate subscription");
        }
        return response.data.data;
    },
};

// ============================================================================
// Super Admin Notes Types & API
// ============================================================================

export interface SuperAdminNote {
    id: number;
    content: string | null;
    color: string;
    isCompleted: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface SuperAdminNoteCreateRequest {
    content?: string;
    color?: string;
}

export interface SuperAdminNoteUpdateRequest {
    content?: string;
    color?: string;
    isCompleted?: boolean;
}

export const superAdminNoteApi = {
    getAll: () =>
        apiClient.get<ApiResponse<SuperAdminNote[]>>("/notes"),

    create: (data: SuperAdminNoteCreateRequest) =>
        apiClient.post<ApiResponse<SuperAdminNote>>("/notes", data),

    update: (id: number, data: SuperAdminNoteUpdateRequest) =>
        apiClient.put<ApiResponse<SuperAdminNote>>(`/notes/${id}`, data),

    delete: (id: number) =>
        apiClient.delete<ApiResponse<void>>(`/notes/${id}`),
};

// ============================================================================
// Shared Media (통합 자료실) Types & API
// ============================================================================

export type SharedMediaType = 'text' | 'video' | 'image' | 'file';
export type SharedMediaVisibility = 'global' | 'company_all' | 'learning_center_all' | 'specific';

export interface SharedMediaResponse {
    id: number;
    title: string;
    description: string | null;
    mediaType: SharedMediaType;
    content: string | null;
    s3Key: string | null;
    originalFilename: string | null;
    contentType: string | null;
    fileSize: number;
    durationSeconds: number | null;
    thumbnailUrl: string | null;
    streamUrl: string | null;
    organizationId: number | null;
    visibility: SharedMediaVisibility;
    status: string;
    createdAt: string;
    updatedAt: string;
    accessOrganizations?: SharedMediaAccessOrg[];
}

export interface SharedMediaAccessOrg {
    organizationId: number;
    organizationName: string;
    subdomain: string | null;
    grantedAt: string;
}

export interface SharedMediaCreateRequest {
    title: string;
    description?: string;
    content?: string;
    visibility?: SharedMediaVisibility;
}

export interface SharedMediaUpdateRequest {
    title?: string;
    description?: string;
    content?: string;
    durationSeconds?: number;
    visibility?: SharedMediaVisibility;
}

export const sharedMediaApi = {
    reorder: (ids: number[], previousIds: number[]) =>
        apiClient.put<ApiResponse<unknown>>("/admin/shared-media/order", { ids, previousIds }),

    getAll: (type?: SharedMediaType) =>
        apiClient.get<ApiResponse<SharedMediaResponse[]>>("/admin/shared-media", {
            params: type ? { type } : undefined,
        }),

    getById: (id: number) =>
        apiClient.get<ApiResponse<SharedMediaResponse>>(`/admin/shared-media/${id}`),

    createText: (data: SharedMediaCreateRequest) =>
        apiClient.post<ApiResponse<SharedMediaResponse>>("/admin/shared-media/text", data),

    uploadFile: (
        file: File,
        title: string,
        mediaType: 'video' | 'image' | 'file',
        visibility: SharedMediaVisibility,
        description?: string,
    ) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("title", title);
        formData.append("mediaType", mediaType);
        formData.append("visibility", visibility);
        if (description) formData.append("description", description);
        return apiClient.post<ApiResponse<SharedMediaResponse>>("/admin/shared-media/upload", formData, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 300000,
        });
    },

    update: (id: number, data: SharedMediaUpdateRequest) =>
        apiClient.put<ApiResponse<SharedMediaResponse>>(`/admin/shared-media/${id}`, data),

    delete: (id: number) =>
        apiClient.delete<ApiResponse<void>>(`/admin/shared-media/${id}`),

    getStreamUrl: (id: number) =>
        apiClient.get<ApiResponse<{ url: string }>>(`/admin/shared-media/${id}/stream-url`),

    // 접근 제어
    getAccess: (id: number) =>
        apiClient.get<ApiResponse<SharedMediaAccessOrg[]>>(`/admin/shared-media/${id}/access`),

    grantAccess: (id: number, organizationIds: number[]) =>
        apiClient.post<ApiResponse<SharedMediaAccessOrg[]>>(`/admin/shared-media/${id}/access`, { organizationIds }),

    revokeAccess: (id: number, orgId: number) =>
        apiClient.delete<ApiResponse<void>>(`/admin/shared-media/${id}/access/${orgId}`),

    updateVisibility: (id: number, visibility: SharedMediaVisibility) =>
        apiClient.put<ApiResponse<SharedMediaResponse>>(`/admin/shared-media/${id}/visibility`, { visibility }),
};

// ============================================================================
// Content Hierarchy Types
// ============================================================================

export interface ContentHierarchyNode {
    id: number;
    title: string;
    bareTitle: string | null;
    type: string; // curriculum, course, module, lesson
    description: string | null;
    learningObjectives: string | null;
    thumbnailUrl: string | null;
    qid?: string | null;
    sequenceOrder: number;
    estimatedDurationMinutes: number | null;
    lcCumulativeAssessmentEnabled?: boolean;
    children: ContentHierarchyNode[];
}

export interface ContentHierarchyResponse {
    id: number;
    title: string;
    bareTitle: string | null;
    type: string;
    description: string | null;
    learningObjectives: string | null;
    thumbnailUrl: string | null;
    estimatedDurationMinutes: number | null;
    lcCumulativeAssessmentEnabled?: boolean;
    children: ContentHierarchyNode[];
}

// ============================================================================
// Contract Types & API
// ============================================================================

export interface AdminContractContactDto {
    userId: number;
    name: string;
    email: string | null;
    phone: string | null;
}

export interface AdminPaymentDto {
    paymentId: string;
    paidAt: string | null;
    amount: number;
    paymentMethod: string;
    paymentStatus: string;
    notes: string | null;
}

export interface AdminContractResponse {
    contractId: number | null;
    contractContact: AdminContractContactDto | null;
    contractStatus: string;
    contractAmount: number | null;
    startDate: string | null;
    endDate: string | null;
    notes: string | null;
    totalPaidAmount: number;
    payments: AdminPaymentDto[];
    lcCenterStatus: AdminLcCenterStatus | null;
    lcOpenedOn: string | null;
}

export interface AdminContractRequest {
    buyerUserId?: number;
    contractStatus?: string;
    totalAmount?: number;
    startDate?: string;
    endDate?: string;
    notes?: string;
}

export interface AdminPaymentRequest {
    paidAt: string;
    amount: number;
    paymentMethod: string;
    notes?: string;
}

export interface AdminLcCenterOperationsRequest {
    status: AdminLcCenterStatus;
    openedOn: string | null;
}

// ============================================================================
// Curriculum Builder API
// ============================================================================

export const curriculumBuilderApi = {
    createContent: async (data: CreateDirectContentRequest): Promise<AdminContent> => {
        const response = await apiClient.post<ApiResponse<AdminContent>>(
            "/admin/curriculum-builder/contents",
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to create content");
        }
        return response.data.data;
    },

    updateContent: async (id: number, data: UpdateDirectContentRequest): Promise<AdminContent> => {
        const response = await apiClient.put<ApiResponse<AdminContent>>(
            `/admin/curriculum-builder/contents/${id}`,
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to update content");
        }
        return response.data.data;
    },

    deleteContent: async (id: number): Promise<void> => {
        await apiClient.delete(`/admin/curriculum-builder/contents/${id}`);
    },

    addLessonsToParent: async (parentId: number, lessonIds: number[]): Promise<AdminContent[]> => {
        const response = await apiClient.post<ApiResponse<AdminContent[]>>(
            `/admin/curriculum-builder/contents/${parentId}/lessons`,
            { lessonIds }
        );
        if (!response.data.data) {
            throw new Error("Failed to add lessons");
        }
        return response.data.data;
    },

    attachChildrenToParent: async (parentId: number, childIds: number[]): Promise<AdminContent[]> => {
        const response = await apiClient.post<ApiResponse<AdminContent[]>>(
            `/admin/curriculum-builder/contents/${parentId}/children`,
            { childIds }
        );
        if (!response.data.data) {
            throw new Error("Failed to attach children");
        }
        return response.data.data;
    },

    cloneChildrenToParent: async (parentId: number, childIds: number[]): Promise<AdminContent[]> => {
        const response = await apiClient.post<ApiResponse<AdminContent[]>>(
            `/admin/curriculum-builder/contents/${parentId}/children/clone`,
            { childIds }
        );
        if (!response.data.data) {
            throw new Error("Failed to clone children");
        }
        return response.data.data;
    },

    removeChildFromParent: async (parentId: number, childId: number): Promise<void> => {
        await apiClient.delete(
            `/admin/curriculum-builder/contents/${parentId}/children/${childId}`
        );
    },

    reorderChildren: async (parentId: number, childOrders: ChildOrderItem[]): Promise<void> => {
        await apiClient.put(
            `/admin/curriculum-builder/contents/${parentId}/children/reorder`,
            { childOrders }
        );
    },

    searchLessons: async (params: {
        keyword?: string;
        page?: number;
        size?: number;
    } = {}): Promise<PageResponse<AdminContent>> => {
        const response = await apiClient.get<ApiResponse<PageResponse<AdminContent>>>(
            "/admin/curriculum-builder/lessons/search",
            { params }
        );
        if (!response.data.data) {
            throw new Error("Failed to search lessons");
        }
        return response.data.data;
    },
};

export const contractAdminApi = {
    getContract: (orgId: number) =>
        apiClient.get<ApiResponse<AdminContractResponse>>(`/admin/organizations/${orgId}/contract`),

    createContract: (orgId: number, data: AdminContractRequest) =>
        apiClient.post<ApiResponse<AdminContractResponse>>(`/admin/organizations/${orgId}/contract`, data),

    updateContract: (orgId: number, data: AdminContractRequest) =>
        apiClient.put<ApiResponse<AdminContractResponse>>(`/admin/organizations/${orgId}/contract`, data),

    updateLcCenterOperations: (orgId: number, data: AdminLcCenterOperationsRequest) =>
        apiClient.put<ApiResponse<{ status: AdminLcCenterStatus; openedOn: string | null }>>(
            `/admin/organizations/${orgId}/contract/lc-operations`,
            data,
        ),

    addPayment: (orgId: number, data: AdminPaymentRequest) =>
        apiClient.post<ApiResponse<AdminContractResponse>>(`/admin/organizations/${orgId}/contract/payments`, data),

    updatePayment: (orgId: number, paymentId: string, data: AdminPaymentRequest) =>
        apiClient.put<ApiResponse<AdminContractResponse>>(`/admin/organizations/${orgId}/contract/payments/${paymentId}`, data),

    deletePayment: (orgId: number, paymentId: string) =>
        apiClient.delete<ApiResponse<AdminContractResponse>>(`/admin/organizations/${orgId}/contract/payments/${paymentId}`),
};
